import * as B from '@blueprintjs/core';
import * as S from '@blueprintjs/select';
import classNames from 'classnames';
import React from 'react';

import layerDialogStyles from 'app/components/DeclarativeMap/ManageLayersDialog.module.scss';
import PolygonFilterIcon from 'app/components/DeclarativeMap/PolygonFilterIcon';
import {
  StyleableAttribute,
  useStyleableAttributes,
} from 'app/components/DeclarativeMap/useStyleableAttributes';
import {OverlayFilter, OverlayListKey, OverlaySelection} from 'app/modules/Remote/Feature/types';
import {GeometryOverlaySetting} from 'app/modules/Remote/FeatureCollection';
import {alphanumericSort} from 'app/utils/featureUtils';

import {MANAGED_OVERLAY_LABEL} from './alertPolicyUtils';
import cs from './styles.module.scss';
import {AlertFormAction, MAX_OVERLAYS_PER_POLICY} from './useAlertForm';

type ProjectOverlay = {id: number; name: string};

export interface OverlayListSelectorProps {
  projectOverlays: ProjectOverlay[];
  // The organization's whole overlay library, which names an overlay taken off the portfolio.
  organizationOverlays: ProjectOverlay[];
  overlaySettings?: Map<string, GeometryOverlaySetting>;
  includeOverlays: OverlaySelection[];
  excludeOverlays: OverlaySelection[];
  dispatch: React.Dispatch<AlertFormAction>;
  disabled: boolean;
}

const LIST_LABELS: Record<OverlayListKey, string> = {
  includeOverlays: 'Only monitor inside any of these',
  excludeOverlays: 'Never monitor inside any of these',
};

const EMPTY_LIST_TEXT: Record<OverlayListKey, string> = {
  includeOverlays: 'None: the whole property is monitored, minus any exclusions below.',
  excludeOverlays: 'None',
};

const DEFAULT_SWATCH_COLOR = '#2e2e2e';

export const OverlayListSelector: React.FC<OverlayListSelectorProps> = ({
  projectOverlays,
  organizationOverlays,
  overlaySettings,
  includeOverlays,
  excludeOverlays,
  dispatch,
  disabled,
}) => {
  const listedIds = new Set(
    [...includeOverlays, ...excludeOverlays].map((selection) => selection.featureCollectionId)
  );
  const candidates = projectOverlays
    .filter((overlay) => !listedIds.has(overlay.id))
    .sort((a, b) => alphanumericSort(a.name, b.name));
  const atCap = listedIds.size >= MAX_OVERLAYS_PER_POLICY;

  const sectionProps = {
    projectOverlays,
    organizationOverlays,
    overlaySettings,
    candidates,
    atCap,
    dispatch,
    disabled,
  };
  return (
    <div>
      <OverlayListSection {...sectionProps} list="includeOverlays" selections={includeOverlays} />
      <OverlayListSection {...sectionProps} list="excludeOverlays" selections={excludeOverlays} />
      <div className={cs.overlayListCaption}>
        {listedIds.size} of {MAX_OVERLAYS_PER_POLICY} overlays used
      </div>
    </div>
  );
};

const OverlayListSection: React.FC<{
  list: OverlayListKey;
  selections: OverlaySelection[];
  projectOverlays: ProjectOverlay[];
  organizationOverlays: ProjectOverlay[];
  overlaySettings?: Map<string, GeometryOverlaySetting>;
  candidates: ProjectOverlay[];
  atCap: boolean;
  dispatch: React.Dispatch<AlertFormAction>;
  disabled: boolean;
}> = ({
  list,
  selections,
  projectOverlays,
  organizationOverlays,
  overlaySettings,
  candidates,
  atCap,
  dispatch,
  disabled,
}) => {
  const colorOf = (overlay: ProjectOverlay) =>
    overlaySettings?.get(overlay.name)?.color || DEFAULT_SWATCH_COLOR;
  const cannotAdd = disabled || atCap || candidates.length === 0;

  return (
    <div className={cs.overlayListSection}>
      <div className={cs.overlayListHeader}>
        <span>{LIST_LABELS[list]}</span>
        <B.Popover
          minimal
          position={B.Position.BOTTOM_LEFT}
          disabled={cannotAdd}
          content={
            <div style={{maxHeight: 300, maxWidth: 300, overflowY: 'auto', padding: '0.5rem'}}>
              {candidates.map((overlay) => (
                <div
                  key={overlay.id}
                  className={layerDialogStyles.overlayRow}
                  onClick={() =>
                    dispatch({type: 'ADD_OVERLAY', list, featureCollectionId: overlay.id})
                  }
                >
                  <div className={layerDialogStyles.colorSwatchWrapper}>
                    <PolygonFilterIcon color={colorOf(overlay)} fill size={24} />
                  </div>
                  <div className={layerDialogStyles.overlayName}>{overlay.name}</div>
                </div>
              ))}
            </div>
          }
        >
          <B.Tooltip
            content={
              atCap
                ? `A policy can use at most ${MAX_OVERLAYS_PER_POLICY} overlays`
                : 'Every overlay on this portfolio is already listed'
            }
            disabled={disabled || !cannotAdd}
            position={B.Position.RIGHT}
          >
            <B.AnchorButton
              small
              icon="plus"
              rightIcon="caret-down"
              text="Add overlay"
              disabled={cannotAdd}
            />
          </B.Tooltip>
        </B.Popover>
      </div>
      {selections.length === 0 && (
        <div className={cs.overlayListEmpty}>{EMPTY_LIST_TEXT[list]}</div>
      )}
      {selections.map((selection) => {
        const overlay = projectOverlays.find((o) => o.id === selection.featureCollectionId);
        const libraryOverlay = organizationOverlays.find(
          (o) => o.id === selection.featureCollectionId
        );
        return (
          <OverlaySelectionRow
            key={selection.featureCollectionId}
            list={list}
            selection={selection}
            overlay={overlay}
            libraryOverlay={libraryOverlay}
            color={overlay ? colorOf(overlay) : DEFAULT_SWATCH_COLOR}
            dispatch={dispatch}
            disabled={disabled}
          />
        );
      })}
    </div>
  );
};

const describeFilter = (filter: OverlayFilter) =>
  `only where ${filter.attribute} is ${filter.values.join(', ')}`;

const OverlaySelectionRow: React.FC<{
  list: OverlayListKey;
  selection: OverlaySelection;
  overlay: ProjectOverlay | undefined;
  libraryOverlay: ProjectOverlay | undefined;
  color: string;
  dispatch: React.Dispatch<AlertFormAction>;
  disabled: boolean;
}> = ({list, selection, overlay, libraryOverlay, color, dispatch, disabled}) => {
  const {featureCollectionId, filter} = selection;

  const removeButton = (
    <B.Button
      small
      minimal
      icon="cross"
      title="Remove overlay"
      aria-label="Remove overlay"
      disabled={disabled}
      onClick={() => dispatch({type: 'REMOVE_OVERLAY', list, featureCollectionId})}
    />
  );

  // An overlay that is neither on the portfolio nor in the organization's library can't be
  // named, edited or removed here; it goes back to the API as it came.
  if (!overlay && !libraryOverlay) {
    return (
      <div className={cs.overlaySelectionRow}>
        <B.Icon icon="lock" />
        <span className={classNames(cs.overlaySelectionName, cs.overlayManagedLabel)}>
          {MANAGED_OVERLAY_LABEL}
        </span>
        {filter && <span className={cs.overlayManagedLabel}>{describeFilter(filter)}</span>}
      </div>
    );
  }

  // An organization overlay taken off the portfolio keeps its name and can be removed, but its
  // filter is only editable once the overlay is back on the portfolio.
  if (!overlay) {
    return (
      <div className={cs.overlaySelectionRow}>
        <span className={cs.overlaySelectionName} title={libraryOverlay!.name}>
          {libraryOverlay!.name}
        </span>
        {filter && <span>{describeFilter(filter)}</span>}
        {removeButton}
      </div>
    );
  }

  return (
    <div className={cs.overlaySelectionRow}>
      <div className={layerDialogStyles.colorSwatchWrapper}>
        <PolygonFilterIcon color={color} fill size={24} />
      </div>
      <span className={cs.overlaySelectionName} title={overlay.name}>
        {overlay.name}
      </span>
      <OverlayFilterControl
        featureCollectionId={featureCollectionId}
        filter={filter}
        disabled={disabled}
        onChange={(nextFilter) =>
          dispatch({type: 'SET_OVERLAY_FILTER', list, featureCollectionId, filter: nextFilter})
        }
      />
      {removeButton}
    </div>
  );
};

const OverlayFilterControl: React.FC<{
  featureCollectionId: number;
  filter: OverlayFilter | null;
  disabled: boolean;
  onChange: (filter: OverlayFilter | null) => void;
}> = ({featureCollectionId, filter, disabled, onChange}) => {
  const {data: attributes = []} = useStyleableAttributes(featureCollectionId, !disabled);
  // An attribute chosen before any value is picked is no filter yet, so it lives here rather
  // than in the policy. It starts as the stored filter's attribute so that clearing the last
  // value of a saved filter leaves the control open, as it does for one picked this session.
  const [pendingAttribute, setPendingAttribute] = React.useState<string | null>(
    filter?.attribute ?? null
  );

  const usableAttributes = attributes.filter((attribute) => attribute.values.length > 0);
  const activeAttribute = filter?.attribute ?? pendingAttribute;

  if (activeAttribute === null) {
    if (usableAttributes.length === 0) return null;
    return (
      <B.Button
        small
        minimal
        icon="filter"
        text="Filter by attribute"
        disabled={disabled}
        onClick={() => setPendingAttribute(usableAttributes[0].name)}
      />
    );
  }

  // A stored attribute the tiles no longer list still shows, with the values it has.
  const options: StyleableAttribute[] = usableAttributes.some((a) => a.name === activeAttribute)
    ? usableAttributes
    : [...usableAttributes, {name: activeAttribute, values: filter?.values ?? []}];
  const selectedValues = filter?.values ?? [];
  const valueItems = Array.from(
    new Set([...(options.find((a) => a.name === activeAttribute)?.values ?? []), ...selectedValues])
  );
  const setValues = (values: string[]) => onChange({attribute: activeAttribute, values});

  return (
    <div className={cs.overlayFilterControls}>
      <span>only where</span>
      <S.Select<StyleableAttribute>
        items={options}
        filterable={false}
        disabled={disabled}
        itemRenderer={(attribute, {handleClick, modifiers}) => (
          <B.MenuItem
            key={attribute.name}
            active={modifiers.active}
            onClick={handleClick}
            text={attribute.name}
          />
        )}
        onItemSelect={(attribute) => {
          setPendingAttribute(attribute.name);
          onChange(null);
        }}
        popoverProps={{minimal: true}}
      >
        <B.Button small text={activeAttribute} rightIcon="caret-down" disabled={disabled} />
      </S.Select>
      <span>is</span>
      <S.MultiSelect<string>
        className={cs.overlayFilterValues}
        items={valueItems}
        selectedItems={selectedValues}
        placeholder="Select values…"
        resetOnSelect
        disabled={disabled}
        itemRenderer={(value, {handleClick, modifiers}) => (
          <B.MenuItem
            key={value}
            text={value}
            icon={selectedValues.includes(value) ? 'tick' : 'blank'}
            active={modifiers.active}
            onClick={handleClick}
          />
        )}
        itemPredicate={(query, value) =>
          !query || value.toLowerCase().includes(query.toLowerCase())
        }
        tagRenderer={(value) => value}
        onItemSelect={(value) =>
          setValues(
            selectedValues.includes(value)
              ? selectedValues.filter((selected) => selected !== value)
              : [...selectedValues, value]
          )
        }
        onRemove={(value) => setValues(selectedValues.filter((selected) => selected !== value))}
        popoverProps={{minimal: true}}
      />
    </div>
  );
};
